import React, { useMemo, useState } from 'react';
import { Tag, Collapse, Image, Alert, Typography, Space, Empty } from 'antd';
import {
  ToolOutlined,
  FileTextOutlined,
  WarningOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import SuggestedPartsList, { ShopNotePart, LinePartRef } from './SuggestedPartsList';

const { Text, Paragraph } = Typography;

/**
 * The shop-only briefing for a line — what the people doing the work need.
 *
 * Rendered from `shop_notes` (migration 199), which is stripped from every
 * customer payload. It is deliberately NOT the same thing as the line's `notes`:
 * that field goes to the customer, and until this existed the vendor lookup was
 * writing procedure coverage, tool lists and scrape gaps into it, where customers
 * read them.
 *
 * The layout follows the order a technician needs things:
 *
 *   1. Special tools   — before the truck goes on the lift, not after
 *   2. Suggested parts — Needed and Recommended, each with the reason it is there
 *   3. Gaps            — where the data is incomplete, so nobody trusts a short list
 *   4. Procedure       — the whole job, in order, with the diagrams inline
 *
 * The parts are a SUGGESTION. Nothing here reaches the estimate or work order
 * until the advisor presses Add on that row — the lookup used to write its whole
 * list onto the ticket, which is what made every job over-quote.
 *
 * The procedure is the point. Following a repair by clicking between a dozen
 * The vendor articles is how a step gets skipped, and the shop already pays for the
 * data — so it is reproduced here in full rather than linked.
 */

export interface ShopNoteSection {
  heading: string;
  items: string[];
  tone?: 'warning';
}

/**
 * The suggestion shape, and the matching helpers, live with the list that
 * renders them. Re-exported here because this is where callers already import
 * the briefing's types from.
 */
export type { ShopNotePart, LinePartRef };

export interface ShopNoteBlock {
  kind: 'step' | 'note' | 'notice' | 'torque' | 'heading' | 'figure';
  text?: string;
  /** Local copy saved at scrape time — renders without a vendor login. */
  imagePath?: string;
  /** The original, only usable by someone signed in to the vendor. */
  imageUrl?: string;
}

export interface ShopNoteProcedure {
  title: string;
  url: string;
  blocks: ShopNoteBlock[];
}

export interface ShopNotes {
  source: 'alldata';
  capturedAt?: string;
  sections: ShopNoteSection[];
  specialTools: string[];
  partsNeeded: ShopNotePart[];
  proceduresScraped: string[];
  procedures: ShopNoteProcedure[];
}

/**
 * Parse the stored column.
 *
 * Tolerates anything that is not our JSON — the column is not reserved for this
 * feature, and someone typing free text into it must not blank the screen.
 */
export function parseShopNotes(raw: unknown): ShopNotes | null {
  if (!raw) return null;
  if (typeof raw === 'object') return normalise(raw as Record<string, unknown>);
  if (typeof raw !== 'string') return null;
  try {
    return normalise(JSON.parse(raw));
  } catch {
    return null;
  }
}

function normalise(v: Record<string, unknown> | null): ShopNotes | null {
  if (!v || typeof v !== 'object') return null;
  if (!Array.isArray((v as any).sections)) return null;
  // Rows written before a field existed must still render.
  return {
    source: 'alldata',
    capturedAt: (v as any).capturedAt,
    sections: (v as any).sections ?? [],
    specialTools: (v as any).specialTools ?? [],
    partsNeeded: (v as any).partsNeeded ?? [],
    proceduresScraped: (v as any).proceduresScraped ?? [],
    procedures: (v as any).procedures ?? [],
  };
}

/** Where the saved diagrams are served from. */
const figureSrc = (b: ShopNoteBlock): string | null => {
  if (!b.imagePath) return null;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  return `${base}/${b.imagePath.replace(/^\/+/, '')}`;
};

const BlockView: React.FC<{ block: ShopNoteBlock; procedureUrl: string }> = ({
  block,
  procedureUrl,
}) => {
  if (block.kind === 'figure') {
    const src = figureSrc(block);
    if (src) {
      return (
        <div style={{ margin: '8px 0 12px' }}>
          <Image
            src={src}
            alt="Procedure diagram"
            style={{ maxWidth: '100%', border: '1px solid #e5e7eb', borderRadius: 4 }}
            placeholder
          />
        </div>
      );
    }
    // The copy could not be saved. Say where to find it rather than showing a
    // broken image — the original URL needs a vendor session this browser
    // does not have.
    return (
      <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '6px 0' }}>
        <PictureOutlined /> Diagram not saved — open the procedure at{' '}
        <a href={procedureUrl} target="_blank" rel="noreferrer">
          alldata.com
        </a>{' '}
        to view it.
      </Text>
    );
  }

  if (!block.text) return null;

  if (block.kind === 'heading') {
    return (
      <Text strong style={{ display: 'block', marginTop: 14, fontSize: 13 }}>
        {block.text}
      </Text>
    );
  }

  // A NOTICE changes what the technician does — "may result in serious engine
  // damage" is not an ordinary step and must not read like one.
  if (block.kind === 'notice') {
    return <Alert type="error" showIcon message={block.text} style={{ margin: '8px 0' }} />;
  }
  if (block.kind === 'note') {
    return <Alert type="info" showIcon message={block.text} style={{ margin: '8px 0' }} />;
  }
  if (block.kind === 'torque') {
    return (
      <Tag color="blue" style={{ margin: '4px 0' }}>
        {block.text}
      </Tag>
    );
  }
  return (
    <Paragraph style={{ marginBottom: 6, fontSize: 13 }}>{block.text}</Paragraph>
  );
};

export interface ShopNotesPanelProps {
  /** The raw `shop_notes` column, or an already-parsed object. */
  value: unknown;
  /** Rendered inside an already-open container, so no outer card. */
  bare?: boolean;
  /**
   * The line's current parts, so a suggestion already on the ticket reads as
   * added. Passed in rather than fetched — the screen above is already holding
   * (and editing) this list, and a fetch in here would race it.
   */
  partsOnLine?: LinePartRef[];
  /** Omit both handlers to render the briefing read-only, with no controls. */
  onAddPart?: (part: ShopNotePart) => void;
  onRemovePart?: (part: ShopNotePart, onLine: LinePartRef) => void;
}

const ShopNotesPanel: React.FC<ShopNotesPanelProps> = ({
  value,
  bare,
  partsOnLine,
  onAddPart,
  onRemovePart,
}) => {
  const notes = useMemo(() => parseShopNotes(value), [value]);
  const [openProcedures, setOpenProcedures] = useState<string[]>([]);

  if (!notes) return null;

  const warnings = notes.sections.filter((s) => s.tone === 'warning');
  const gapSection = warnings.find((s) => /incomplete/i.test(s.heading));
  const figureCount = notes.procedures.reduce(
    (n, p) => n + p.blocks.filter((b) => b.kind === 'figure').length,
    0
  );

  const body = (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Text type="secondary" style={{ fontSize: 11 }}>
        Shop only — not shown to the customer
        {notes.capturedAt ? ` · captured ${new Date(notes.capturedAt).toLocaleDateString()}` : ''}
      </Text>

      {/* The gap warning first: a short parts list that looks complete is the
          most expensive thing on this panel. */}
      {gapSection && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={gapSection.heading}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {gapSection.items.map((i) => (
                <li key={i} style={{ fontSize: 12 }}>
                  {i}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {notes.specialTools.length > 0 && (
        <div>
          <Text strong style={{ fontSize: 13 }}>
            <ToolOutlined /> Special tools required
          </Text>
          <div style={{ marginTop: 6 }}>
            {notes.specialTools.map((t) => (
              <Tag color="orange" key={t} style={{ marginBottom: 4 }}>
                {t}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {notes.partsNeeded.length > 0 && (
        <div>
          <Text strong style={{ fontSize: 13 }}>
            Suggested parts ({notes.partsNeeded.length})
          </Text>
          <div style={{ color: '#6b7280', fontSize: 11 }}>
            Nothing is on the ticket until you add it.
          </div>
          <SuggestedPartsList
            parts={notes.partsNeeded}
            partsOnLine={partsOnLine}
            onAdd={onAddPart}
            onRemove={onRemovePart}
          />
        </div>
      )}

      {notes.proceduresScraped.length > 0 && (
        <div>
          <Text strong style={{ fontSize: 13 }}>
            Procedures scraped ({notes.proceduresScraped.length})
          </Text>
          <div style={{ marginTop: 4, color: '#6b7280', fontSize: 12 }}>
            {notes.proceduresScraped.join(' · ')}
          </div>
        </div>
      )}

      {notes.procedures.length > 0 ? (
        <div>
          <Text strong style={{ fontSize: 13 }}>
            <FileTextOutlined /> Procedure — {notes.procedures.length} article
            {notes.procedures.length === 1 ? '' : 's'}
            {figureCount > 0 ? `, ${figureCount} diagram${figureCount === 1 ? '' : 's'}` : ''}
          </Text>
          <Collapse
            style={{ marginTop: 6 }}
            activeKey={openProcedures}
            onChange={(k) => setOpenProcedures(k as string[])}
            items={notes.procedures.map((p, i) => ({
              key: String(i),
              label: <span style={{ fontSize: 13 }}>{p.title}</span>,
              children: (
                <div>
                  {p.blocks.map((b, j) => (
                    <BlockView key={j} block={b} procedureUrl={p.url} />
                  ))}
                </div>
              ),
            }))}
          />
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No procedure captured for this line"
        />
      )}
    </Space>
  );

  if (bare) return body;

  return (
    <div
      style={{
        background: '#fafafa',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: 12,
        marginTop: 8,
      }}
    >
      {body}
    </div>
  );
};

export default ShopNotesPanel;
